import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AmcLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import {
  AmcPathingAnalysisResponse,
  AmcPathingAttributionRow,
  AmcPathingCampaignPathRow,
  AmcPathingOccurrenceRow,
  AmcPathingTouchpointRow
} from '../amc-pathing-analysis-types';

const sanitizeTouchpointRow = (row: AmcPathingTouchpointRow): AmcPathingTouchpointRow => ({
  ...row,
  totalPurchases: Number(row.totalPurchases),
  newToBrandTotalPurchases: Number(row.newToBrandTotalPurchases)
});

const sanitizeOccurrenceRow = (row: AmcPathingOccurrenceRow): AmcPathingOccurrenceRow => ({
  ...row,
  userThatPurchased: Number(row.userThatPurchased)
});

// The raw path escapes every comma with a backslash (e.g. "1-Display\, 2-SP"), presumably an
// artifact of AMC's own export — strip it so both the displayed path and the ad-type filter
// matching (which splits on ",") see clean "1-Display, 2-SP" segments.
const sanitizeCampaignPathRow = (row: AmcPathingCampaignPathRow): AmcPathingCampaignPathRow => ({
  ...row,
  path: row.path.replace(/\\/g, ''),
  reach: Number(row.reach),
  impressions: Number(row.impressions),
  totalCost: Number(row.totalCost),
  totalPurchases: Number(row.totalPurchases),
  totalProductSales: Number(row.totalProductSales),
  userThatPurchased: Number(row.userThatPurchased)
});

const sanitizeAttributionRow = (row: AmcPathingAttributionRow): AmcPathingAttributionRow => ({
  ...row,
  spend: Number(row.spend),
  totalProductSales: Number(row.totalProductSales),
  usersThatConverted: Number(row.usersThatConverted)
});

const fetchAmcPathingAnalysisData = async (
  amcInstanceId: string,
  startDate: string,
  endDate: string
): Promise<AmcPathingAnalysisResponse> => {
  const response = await AmcLambdaFetch('/getPathingAnalysisReport', {
    params: { amcInstanceId, startDate, endDate, duration: 'monthly' }
  });
  const json = await awaitJSON<AmcPathingAnalysisResponse>(response);
  return {
    ...json,
    adTypeTouchpointData: json.adTypeTouchpointData.map(sanitizeTouchpointRow),
    campaignAndAdTypeOccurrenceData:
      json.campaignAndAdTypeOccurrenceData.map(sanitizeOccurrenceRow),
    campaignAndAdTypePathData: json.campaignAndAdTypePathData.map(sanitizeCampaignPathRow),
    firstLastLinearAndPositionCombinedData:
      json.firstLastLinearAndPositionCombinedData.map(sanitizeAttributionRow)
  };
};

export const useAmcPathingAnalysisData = (
  amcInstanceId: string | undefined,
  startDate: string,
  endDate: string
): ReturnType<typeof useQuery<AmcPathingAnalysisResponse>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['amcPathingAnalysisData', amcInstanceId, startDate, endDate],
    queryFn: () => {
      if (!amcInstanceId) throw new Error('Missing AMC instance id');
      return fetchAmcPathingAnalysisData(amcInstanceId, startDate, endDate);
    },
    enabled: Boolean(amcInstanceId),
    placeholderData: keepPreviousData
  });
